import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  departmentsTooltip,
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
const YES_NO = ["Yes", "No"];

const SpaPaymentMethods = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [paymentMethodName, setPaymentMethodName] = useState<string | null>(
    null,
  );
  const [active, setActive] = useState<string>("Yes");
  const [allowChange, setAllowChange] = useState<string>("No");
  const [outletCode, setOutletCode] = useState<string | null>(null);
  const [defaultMethod, setDefaultMethod] = useState<string>("No");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [paymentMethodNameFieldError, setPaymentMethodNameFieldError] =
    useState<boolean>(false);
  const [
    paymentMethodNameFieldErrorMessage,
    setPaymentMethodNameFieldErrorMessage,
  ] = useState<string>("");

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validatePaymentMethodFields = (): boolean => {
    if (!paymentMethodName || paymentMethodName.length === 0) {
      setAlertMessage("Payment Method Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setPaymentMethodNameFieldError(false);
    setPaymentMethodNameFieldErrorMessage("");
    if (
      !validateString(
        paymentMethodName,
        validationLength.spaPaymentMethod,
        validationType.NAME,
      )
    ) {
      setPaymentMethodNameFieldError(true);
      setPaymentMethodNameFieldErrorMessage(
        validationMessages.spaPaymentMethod,
      );
      return false;
    }
    return true;
  };

  const updateItem = () => {
    if (!validatePaymentMethodFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.paymentMethods} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validatePaymentMethodFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setPaymentMethodName(list["Payment Method Name"] ?? null);
      setActive(list?.Active ?? "Yes");
      setAllowChange(list["Allow Change"] ?? "No");
      setOutletCode(list["Outlet Code"] ?? null);
      setDefaultMethod(list?.Default ?? "No");
    } else {
      setPaymentMethodName(null);
      setPaymentMethodNameFieldError(false);
      setPaymentMethodNameFieldErrorMessage("");
      setActive("Yes");
      setAllowChange("No");
      setOutletCode(null);
      setDefaultMethod("No");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Payment Method Name": paymentMethodName,
    Active: active,
    "Allow Change": allowChange,
    "Outlet Code": outletCode,
    Default: defaultMethod,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Payment Methods
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.paymentMethodHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Payment Methods",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Payment Method Name *</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.paymentMethodPaymentMethodName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Payment Method Name"
              value={paymentMethodName ?? ""}
              onChange={(e) => setPaymentMethodName(e.target.value || null)}
              fullWidth
              helperText={
                paymentMethodNameFieldError
                  ? paymentMethodNameFieldErrorMessage
                  : ""
              }
              error={paymentMethodNameFieldError}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Active</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.paymentMethodActive)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              options={YES_NO}
              value={active}
              onChange={(_, v) => setActive(v ?? "Yes")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Allow Change</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.paymentMethodAllowChange,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              options={YES_NO}
              value={allowChange}
              onChange={(_, v) => setAllowChange(v ?? "No")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Outlet Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.paymentMethodOutletCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              size="small"
              value={outletCode ?? ""}
              onChange={(e) => setOutletCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Default</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.paymentMethodDefault)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Autocomplete
              options={YES_NO}
              value={defaultMethod}
              onChange={(_, v) => setDefaultMethod(v ?? "No")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Payment Method
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Payment Methods Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Payment Method Name"
              name="Active"
            />
          ) : (
            <Typography variant="body2">
              No Payment Methods Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaPaymentMethods;
